if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.isPdpV2 = Boolean(this.closest('.pdp-v2'));
        this.mql = window.matchMedia('(min-width: 768px)');
        if (!this.elements.thumbnails) return;
        if (this.isPdpV2) {
          this.addEventListener('click', this.handlePdpControlClick.bind(this), true);
          this.elements.viewer.addEventListener('pointerdown', this.handlePdpPointerDown.bind(this), { passive: true });
          this.elements.viewer.addEventListener('pointermove', this.handlePdpPointerMove.bind(this), { passive: true });
          this.elements.viewer.addEventListener('pointerup', this.handlePdpPointerUp.bind(this), { passive: true });
          this.elements.viewer.addEventListener('mousedown', this.handlePdpPointerDown.bind(this));
          this.elements.viewer.addEventListener('mousemove', this.handlePdpPointerMove.bind(this));
          this.elements.viewer.addEventListener('mouseup', this.handlePdpPointerUp.bind(this));
          this.syncPdpMediaState();
        }
        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), this.isPdpV2 ? 80 : 500)
        );
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.elements.viewer.slider) {
          this.elements.viewer.slider.addEventListener('scroll', this.onViewerScroll.bind(this), { passive: true });
          requestAnimationFrame(() => this.syncActiveMediaFromScroll());
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      get usesDesktopVerticalGallery() {
        return this.elements.thumbnails.dataset.axis === 'vertical' && window.matchMedia('(min-width: 992px)').matches;
      }

      onViewerScroll() {
        if (!this.usesDesktopVerticalGallery || this.viewerScrollFrame) return;
        this.viewerScrollFrame = requestAnimationFrame(() => {
          this.viewerScrollFrame = null;
          this.syncActiveMediaFromScroll();
        });
      }

      syncActiveMediaFromScroll() {
        if (!this.usesDesktopVerticalGallery || !this.elements.viewer.slider) return;

        const slider = this.elements.viewer.slider;
        const mediaItems = Array.from(slider.querySelectorAll('[data-media-id]')).filter(
          (media) => !media.hidden && media.getClientRects().length > 0
        );
        if (!mediaItems.length) return;

        const viewportCenter = slider.scrollTop + slider.clientHeight / 2;
        const activeMedia = mediaItems.reduce((closest, media) => {
          const mediaCenter = media.offsetTop + media.clientHeight / 2;
          const closestCenter = closest.offsetTop + closest.clientHeight / 2;
          return Math.abs(mediaCenter - viewportCenter) < Math.abs(closestCenter - viewportCenter) ? media : closest;
        }, mediaItems[0]);

        if (activeMedia.classList.contains('is-active')) return;
        mediaItems.forEach((media) => media.classList.toggle('is-active', media === activeMedia));

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.playActiveMedia(activeMedia);
      }

      onSlideChanged(event) {
        // Check if currentElement is defined and has a dataset property
        if (!event.detail.currentElement || !event.detail.currentElement.dataset) {
          return;
        }

        if (this.isPdpV2) {
          this.pdpMediaItems.forEach((media) => {
            media.classList.toggle('is-active', media === event.detail.currentElement);
          });
        }

        const mediaId = event.detail.currentElement.dataset.mediaId;

        // Ensure mediaId exists before proceeding
        if (!mediaId) {
          return;
        }

        // Proceed to find the thumbnail
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

        // Check if the thumbnail was found
        if (!thumbnail) {
          return;
        }

        // Set the active thumbnail
        this.setActiveThumbnail(thumbnail);
      }

      get pdpMediaItems() {
        const gallery = this.elements.viewer.querySelector('[id^="Slider-Gallery"]');
        return gallery ? Array.from(gallery.querySelectorAll(':scope > .product__media-item')) : [];
      }

      syncPdpMediaState() {
        const items = this.pdpMediaItems;
        const activeIndex = Math.max(
          0,
          items.findIndex((media) => media.classList.contains('is-active'))
        );
        this.setPdpMediaState(items[activeIndex], activeIndex, false);
      }

      handlePdpControlClick(event) {
        if (!this.isPdpV2) return;
        const control = event.target.closest('button');
        if (!control) return;

        const thumbnail = control.closest('[data-target]');
        if (thumbnail && this.elements.thumbnails?.contains(thumbnail)) {
          const media = this.elements.viewer.querySelector(`[data-media-id="${thumbnail.dataset.target}"]`);
          if (!media) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.setPdpMediaState(media, this.pdpMediaItems.indexOf(media), true);
          return;
        }

        if (!this.elements.viewer.contains(control) || !['next', 'previous'].includes(control.name)) return;
        const items = this.pdpMediaItems;
        const currentIndex = items.findIndex((media) => media.classList.contains('is-active'));
        const nextIndex = control.name === 'next' ? currentIndex + 1 : currentIndex - 1;
        if (!items[nextIndex]) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setPdpMediaState(items[nextIndex], nextIndex, true);
      }

      handlePdpPointerDown(event) {
        if (event.target.closest('.slider-button')) return;
        this.pdpPointerStart = { x: event.clientX, y: event.clientY };
      }

      handlePdpPointerUp(event) {
        if (!this.pdpPointerStart) return;
        this.handlePdpSwipe(event);
        this.pdpPointerStart = null;
      }

      handlePdpPointerMove(event) {
        if (this.pdpPointerStart) this.handlePdpSwipe(event);
      }

      handlePdpSwipe(event) {
        const deltaX = event.clientX - this.pdpPointerStart.x;
        const deltaY = event.clientY - this.pdpPointerStart.y;
        if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        this.pdpPointerStart = null;

        const items = this.pdpMediaItems;
        const currentIndex = items.findIndex((media) => media.classList.contains('is-active'));
        const nextIndex = deltaX < 0 ? currentIndex + 1 : currentIndex - 1;
        if (items[nextIndex]) this.setPdpMediaState(items[nextIndex], nextIndex, true);
      }

      setPdpMediaState(activeMedia, activeIndex, announce) {
        if (!activeMedia) return;
        const items = this.pdpMediaItems;
        items.forEach((media) => media.classList.toggle('is-active', media === activeMedia));

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        if (activeThumbnail) this.setActiveThumbnail(activeThumbnail);
        const currentCounter = this.elements.viewer.querySelector('.slider-counter--current');
        if (currentCounter) currentCounter.textContent = String(activeIndex + 1);
        const previousButton = this.elements.viewer.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('button[name="next"]');
        if (previousButton) previousButton.disabled = activeIndex <= 0;
        if (nextButton) nextButton.disabled = activeIndex >= items.length - 1;
        this.playActiveMedia(activeMedia);
        if (announce && activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) {
          return;
        }
        if (this.isPdpV2) {
          this.setPdpMediaState(activeMedia, this.pdpMediaItems.indexOf(activeMedia), true);
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }
        this.preventStickyHeader();
        if (this.usesDesktopVerticalGallery && this.elements.viewer.slider) {
          this.elements.viewer.slider.scrollTo({
            top: activeMedia.offsetTop,
            behavior: prepend ? 'auto' : 'smooth',
          });
          this.playActiveMedia(activeMedia);
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          return;
        }
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: this.isPdpV2 ? 'auto' : 'smooth',
            });
          }
          if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);
        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        const isVertical =
          this.elements.thumbnails.dataset.axis === 'vertical' && window.matchMedia('(min-width: 992px)').matches;
        this.elements.thumbnails.slider.scrollTo(isVertical ? { top: thumbnail.offsetTop } : { left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
